import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  DialogActions,
  Checkbox,
  FormControlLabel
} from "@mui/material";

interface TermsAndPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function TermsAndPaymentDialog({
  open,
  onClose,
  onAccept
}: TermsAndPaymentDialogProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // ⚡ Stato interno che controlla se il dialog è visibile
  const [isVisible, setIsVisible] = useState(open);

  // Versione termini
  const TERMS_VERSION = "v1.0.0";

  // Mantiene montato il dialog dopo la chiusura per poter animare
  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      // Ritarda lo smontaggio per permettere la chiusura animata
      const t = setTimeout(() => setIsVisible(false), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset stati all'apertura
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }
  }, [open]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    if (bottom && !hasScrolledToBottom) setHasScrolledToBottom(true);
  };

  const handleAccept = async () => {
    const log = {
      acceptedAt: new Date().toISOString(),
      termsVersion: TERMS_VERSION,
      scrolledToBottom: hasScrolledToBottom,
      checkboxAccepted: isChecked,
      userAgent: navigator.userAgent
    };

    console.log("handleAccept: " + JSON.stringify(log));

    // TODO: Endpoint /api/terms/accept non ancora implementato
    // Loggato solo in console per ora
    try {
      // await fetch("/api/terms/accept", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(log)
      // });
      console.log("✅ Termini accettati:", log);
    } catch (err) {
      console.error("Errore salvataggio log termini", err);
    }

    onAccept();
  };

  if (!isVisible) return null; // smontato dopo animazione

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // ❗ Blocca click sullo sfondo durante la chiusura
      BackdropProps={{
        sx: {
          transition: "opacity 0.25s",
          pointerEvents: open ? "auto" : "none"
        }
      }}
      PaperProps={{
        sx: {
          height: "85vh",     // ⬅️ aumenta l’altezza!
          maxHeight: "90vh",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.28)",
          animation: open ? "fadeScaleIn 0.25s ease-out" : "fadeScaleOut 0.25s ease-in",
          "@keyframes fadeScaleIn": {
            "0%": { opacity: 0, transform: "scale(0.92)" },
            "100%": { opacity: 1, transform: "scale(1)" }
          },
          "@keyframes fadeScaleOut": {
            "0%": { opacity: 1, transform: "scale(1)" },
            "100%": { opacity: 0, transform: "scale(0.90)" }
          }
        }
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: "#f9fafb",
          borderBottom: "1px solid #e5e7eb"
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1f2937",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          Termini e Condizioni
          <Box
            sx={{
              px: 1,
              py: 0.2,
              fontSize: "12px",
              borderRadius: "6px",
              bgcolor: "#e5e7eb",
              color: "#374151"
            }}
          >
            {TERMS_VERSION}
          </Box>
        </Typography>
      </Box>

      {/* PDF */}
      <DialogContent dividers sx={{ height: "350px", p: 0 }}>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            height: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#d1d5db",
              borderRadius: "4px"
            }
          }}
        >
          <embed
            src="/Richiesta_estinzione_totaleparziale.pdf"
            type="application/pdf"
            width="100%"
            height="800px"
          />
        </Box>
      </DialogContent>

      {/* CHECKBOX */}
      <Box px={3} py={2} sx={{ background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
        <FormControlLabel
          control={<Checkbox checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} />}
          label="Ho letto e accetto i termini e condizioni"
        />
      </Box>

      {/* ACTIONS */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
        <Button onClick={onClose} variant="outlined">Chiudi</Button>
        <Button variant="contained" disabled={!hasScrolledToBottom || !isChecked} onClick={handleAccept}>
          Accetto
        </Button>
      </DialogActions>
    </Dialog>
  );
}
